// services/kronium-embed/ui/src/config.ts
import type { PartnerConfig } from './types';

// Extend Window interface for injected config
declare global {
  interface Window {
    __PARTNER_CONFIG__?: PartnerConfig;
  }
}

/**
 * Default Kronium config (fallback for local dev and when KV config not injected).
 * Uses Czech market as default.
 */
export const DEFAULT_CONFIG: PartnerConfig = {
  partner_id: 'kronium-cz',
  name: 'Kronium',
  branding: {
    tier: 'themed',
    logo_url: 'https://kronium.eu/skins/KroniumLight/styleImages/logos/fenix.png',
    colors: {
      primary: '#1a73e8',      // Kronium blue
      secondary: '#202124',    // Dark
      background: '#ffffff',   // White
      text: '#202124',         // Dark text
    },
    show_powered_by: false,
  },
  language: 'cs',  // Czech as default
  supported_languages: [
    { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
    { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
    { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
    { code: 'hr', name: 'Hrvatski', flag: '🇭🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
  ],
  contact_form: {
    countries: [
      // Kronium markets
      { code: 'CZ', name: 'Česká republika', phonePrefix: '+420' },
      { code: 'SK', name: 'Slovensko', phonePrefix: '+421' },
      { code: 'HU', name: 'Magyarország', phonePrefix: '+36' },
      { code: 'HR', name: 'Hrvatska', phonePrefix: '+385' },
      // Other EU
      { code: 'AT', name: 'Österreich', phonePrefix: '+43' },
      { code: 'DE', name: 'Deutschland', phonePrefix: '+49' },
      { code: 'PL', name: 'Polska', phonePrefix: '+48' },
      { code: 'SI', name: 'Slovenija', phonePrefix: '+386' },
    ],
    default_country: 'CZ',
  },
  privacy_policy_url: 'https://kronium.eu/privacy',

  // Feature toggles - all disabled by default for Kronium
  features: {
    file_upload: false,
    research_mode: false,
    email_verification: false,
    human_handover: true,  // Enable Chatwoot handover
  },

  // Chatwoot integration
  chatwoot: {
    enabled: true,
    base_url: 'https://chatwoot.ariga.nlk.app',
    inbox_id: undefined,  // Will be set per-tenant
    team_id: undefined,   // Will be set per-tenant
  },
};

/**
 * Get partner config from window (injected by Worker) or fallback to default.
 */
export function getPartnerConfig(): PartnerConfig {
  return window.__PARTNER_CONFIG__ ?? DEFAULT_CONFIG;
}

/**
 * Check if a feature is enabled for the current tenant.
 */
export function isFeatureEnabled(feature: keyof PartnerConfig['features']): boolean {
  const config = getPartnerConfig();
  return config.features?.[feature] ?? false;
}
